import {ComponentRef} from '@angular/core';
import {ContextBarService} from 'src/app/diagram-interaction/context-bar/context-bar.service';

declare let mxEventSource;
declare let mxConstants;
declare let mxEvent;

interface Bounds {
  x: number;
  y: number;
  height: number;
  width: number;
}

export function mxContextBar<T>(
  graph,
  contextBarComponent: ComponentRef<T>,
  contextBarService: ContextBarService
) {
  this.contextBarComponent = contextBarComponent;
  this.contextBarNode = contextBarComponent.location.nativeElement;
  this.contextBarService = contextBarService;
  this.graph = graph;

  this.init();
}

mxContextBar.prototype = new mxEventSource();
mxContextBar.prototype.constructor = mxContextBar;

/**
 * Variable: contextBarMargin
 *
 * Margin between context toolbar and shape.
 */
mxContextBar.prototype.contextBarMargin = 45;

/**
 * Variable: contextBarEdgeMargin
 *
 * Margin between edges of the graph's visible area and context toolbar.
 */
mxContextBar.prototype.contextBarEdgeMargin = 5;

/**
 * Function: recalculatePositionHandler
 *
 * Handler to hide context toolbar during recalculation of its position and prevent flickering.
 */
mxContextBar.prototype.recalculatePositionHandler = function () {
  if (this.graph.isEditing()) {
    return;
  }

  this.contextBarNode.style.visibility = 'hidden';
  clearTimeout(this.redrawTimeout);

  this.redrawTimeout = setTimeout(() => {
    if (!this.contextBarNode) {
      return;
    }

    this.recalculatePosition();
    this.contextBarNode.style.visibility = 'visible';
  }, 400);
};

/**
 * Function: editingHandler
 *
 * Handler to hide context toolbar during cell editing.
 */
mxContextBar.prototype.editingHandler = function () {
  if (!this.contextBarNode) {
    return;
  }

  if (!this.graph.isEditing()) {
    this.recalculatePosition();
    this.contextBarNode.style.visibility = 'visible';
  } else {
    this.contextBarNode.style.visibility = 'hidden';
  }
};

/**
 * Function: init
 *
 * Appends contextBar DOM node to the body and installs the event listeners.
 */
mxContextBar.prototype.init = function () {
  this.contextBarNode.style.position = 'absolute';

  // Should be lower than z-index of material dialogs which is 7
  this.contextBarNode.style.zIndex = 6;

  document.body.appendChild(this.contextBarNode);

  this.recalculatePositionHandler = this.recalculatePositionHandler.bind(this);
  this.editingHandler = this.editingHandler.bind(this);

  this.graph.container.addEventListener('scroll', this.recalculatePositionHandler);
  this.graph.addListener(mxEvent.CELLS_RESIZED, this.recalculatePositionHandler);
  this.graph.addListener(mxEvent.CELLS_MOVED, this.recalculatePositionHandler);
  this.graph.addListener(mxEvent.EDITING_STARTED, this.editingHandler);
  this.graph.addListener(mxEvent.EDITING_STOPPED, this.editingHandler);
};

/**
 * Function: updateOptions
 *
 * Updates context toolbar options.
 */
mxContextBar.prototype.updateOptions = function () {
  this.contextBarComponent.instance.optionGroups = this.contextBarService.getOptions();
  this.contextBarComponent.changeDetectorRef.detectChanges();
};

/**
 * Function: redraw
 *
 * Redraws context toolbar with options update and position recalculation.
 */
mxContextBar.prototype.redraw = function () {
  this.updateOptions();
  this.recalculatePosition();
};

/**
 * Function: getSelectionBounds
 *
 * Returns selection bounds.
 */
mxContextBar.prototype.getSelectionBounds = function (): Bounds {
  const selectedCells = this.graph.getSelectionCells();

  return selectedCells?.length > 1
    ? this.graph.getBoundingBox(selectedCells)
    : this.graph.view.getState(selectedCells[0]);
};

/**
 * Function: recalculatePosition
 *
 * Recalculates context toolbar position.
 */
mxContextBar.prototype.recalculatePosition = function () {
  const bounds = this.getSelectionBounds();

  if (!bounds) {
    return;
  }

  const x = bounds.x - this.graph.container.scrollLeft;
  const y = bounds.y - this.graph.container.scrollTop;

  const leftToolbarWidth =
    mxConstants.LEFT_TOOLBAR_BOUNDS.width + mxConstants.LEFT_TOOLBAR_BOUNDS.x;
  const mainToolbarHeight =
    mxConstants.MAIN_TOOLBAR_BOUNDS.height + mxConstants.MAIN_TOOLBAR_BOUNDS.y;

  const freeSpaceOnLeft = x - (leftToolbarWidth + this.contextBarEdgeMargin);
  const freeSpaceOnRight =
    this.graph.container.offsetWidth - x - bounds.width - this.contextBarEdgeMargin;
  const freeSpaceOnTop = y - (mainToolbarHeight + this.contextBarEdgeMargin);

  this.shapeOutOfVisibleArea =
    freeSpaceOnLeft + this.contextBarEdgeMargin + bounds.width < 0 ||
    freeSpaceOnRight + this.contextBarEdgeMargin + bounds.width < 0 ||
    freeSpaceOnTop + this.contextBarEdgeMargin + bounds.height < 0 ||
    y > this.graph.container.offsetHeight;

  if (this.shapeOutOfVisibleArea) {
    this.contextBarNode.style.display = 'none';

    return;
  }

  this.contextBarNode.style.display = '';

  const contextBarOutsideWidth = (this.contextBarNode.offsetWidth - bounds.width) / 2;
  const spaceForContextBar = this.contextBarMargin + this.contextBarNode.offsetHeight;
  const overlaysBottomToolbar =
    y - this.contextBarMargin + this.contextBarEdgeMargin > mxConstants.BOTTOM_TOOLBAR_BOUNDS.y;

  // Horizontal positioning
  switch (true) {
    case contextBarOutsideWidth > freeSpaceOnLeft:
      this.contextBarNode.style.left = x - freeSpaceOnLeft + 'px';
      break;

    case contextBarOutsideWidth > freeSpaceOnRight:
      this.contextBarNode.style.left =
        x + bounds.width - this.contextBarNode.offsetWidth + freeSpaceOnRight + 'px';
      break;

    default:
      this.contextBarNode.style.left = x - contextBarOutsideWidth + 'px';
      break;
  }

  // Vertical positioning
  switch (true) {
    case spaceForContextBar > freeSpaceOnTop:
      this.contextBarNode.style.top = y + bounds.height + this.contextBarMargin + 'px';
      break;

    case overlaysBottomToolbar:
      this.contextBarNode.style.top =
        mxConstants.BOTTOM_TOOLBAR_BOUNDS.y -
        this.contextBarNode.offsetHeight -
        this.contextBarEdgeMargin +
        'px';
      break;

    default:
      this.contextBarNode.style.top = y - spaceForContextBar + 'px';
      break;
  }
};

/**
 * Function: destroy
 *
 * Destroys ContextBar angular component and instance of mxContextBar, removes event listeners.
 */
mxContextBar.prototype.destroy = function () {
  if (this.contextBarComponent) {
    this.contextBarComponent.destroy();
    this.graph.contextBar = null;
  }

  this.graph.container.removeEventListener('scroll', this.recalculatePositionHandler);
  this.graph.removeListener(this.recalculatePositionHandler);
  this.graph.removeListener(this.editingHandler);
};
